import { flexRender, SortingState, Table } from '@tanstack/react-table';
import { ReactElement } from 'react';

import { safeFormattedIntlString } from '../../../formatters';
import { useIsNewUiEnabled } from '../../../hooks/useIsNewUiEnabled';
import messages from '../messages';

import {
  $HeaderTr,
  $StyledHeader,
  $StyledTh,
  $StyledTHead,
} from './DatatableCore.styles';
import Order from './Order';

interface TableHeaderProps<D> {
  table: Table<D>;
  sorting?: SortingState;
  dataTestId?: string;
}

const Header = <D = unknown,>({
  table,
  sorting,
  dataTestId,
}: TableHeaderProps<D>): ReactElement => {
  'use no memo';

  const isNewUiEnabled = useIsNewUiEnabled();

  return (
    <$StyledTHead
      title={safeFormattedIntlString(messages.multiSortTips)}
      $isNewUiEnabled={isNewUiEnabled}
    >
      {table.getHeaderGroups().map((headerGroup) => {
        return (
          <$HeaderTr key={headerGroup.id}>
            {headerGroup.headers.map((header) => {
              const isSorted = header.column.getIsSorted();

              return (
                <$StyledTh
                  key={header.id}
                  colSpan={header.colSpan}
                  title={undefined}
                  canSortBy={header.column.getCanSort() && !!sorting}
                  sort={isSorted}
                  onClick={header.column.getToggleSortingHandler()}
                  $size={header.column.getSize()}
                  $minSize={header.column.columnDef.minSize}
                  $maxSize={header.column.columnDef.maxSize}
                  $isNewUiEnabled={isNewUiEnabled}
                  data-testid={
                    dataTestId ? `${dataTestId}-${header.id}` : undefined
                  }
                >
                  <$StyledHeader>
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext(),
                    )}

                    {!!sorting && <Order order={isSorted} />}
                  </$StyledHeader>
                </$StyledTh>
              );
            })}
          </$HeaderTr>
        );
      })}
    </$StyledTHead>
  );
};

export default Header;
